import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Avatar,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import {
  Home24Regular,
  Settings24Regular,
  SignOut24Regular,
  Certificate24Regular,
  PeopleTeam24Regular,
  DataTrending24Regular,
} from '@fluentui/react-icons';
import { SPACING, TYPOGRAPHY } from '../styles/constants';

const useStyles = makeStyles({
  container: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  header: {
    backgroundColor: tokens.colorBrandBackground,
    color: 'white',
    padding: `${SPACING.md} ${SPACING.lg}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: tokens.shadow4,
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: SPACING.xs },
  headerTitle: { ...TYPOGRAPHY.h2, color: 'white' },
  headerSubtitle: { ...TYPOGRAPHY.bodySmall, color: 'white', opacity: 0.8 },
  nav: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    width: '220px',
    padding: `${SPACING.lg} 0`,
  },
  navButton: { width: '100%', justifyContent: 'flex-start', padding: `${SPACING.md} ${SPACING.lg}`, marginBottom: SPACING.sm },
  main: { display: 'flex', flex: 1 },
  content: {
    flex: 1,
    padding: SPACING.xl,
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: 'auto',
  },
});

interface Props {
  children: ReactNode;
  companyName?: string;
}

export default function CustomerLayout({ children, companyName }: Props) {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();

  const user = accounts[0];
  const userName = user?.name || user?.username || 'IT Admin';

  const handleLogout = () => {
    instance.logoutPopup({ postLogoutRedirectUri: '/', mainWindowRedirectUri: '/' });
  };

  const navItems = [
    { path: '/customer/dashboard', label: 'Dashboard', icon: <Home24Regular /> },
    { path: '/customer/roi', label: 'ROI', icon: <DataTrending24Regular /> },
    { path: '/customer/settings', label: 'Configuration', icon: <Settings24Regular /> },
    { path: '/customer/licenses', label: 'Licensing & Billing', icon: <Certificate24Regular /> },
    { path: '/customer/users', label: 'Users', icon: <PeopleTeam24Regular /> },
    { path: '/customer/team-members', label: 'Team Members', icon: <PeopleTeam24Regular /> },
  ];

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Text className={styles.headerTitle}>Sherpai</Text>
          {companyName && (
            <Text className={styles.headerSubtitle}>{companyName} — IT Admin Portal</Text>
          )}
        </div>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="transparent"
              icon={<Avatar name={userName} color="colorful" />}
              style={{ color: 'white' }}
            >
              {userName}
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem disabled>
                <div>
                  <div style={{ fontWeight: 600 }}>{userName}</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>{user?.username}</div>
                </div>
              </MenuItem>
              <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>Sign Out</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </header>

      <div className={styles.main}>
        <nav className={styles.nav}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              appearance={location.pathname.startsWith(item.path) ? 'primary' : 'subtle'}
              icon={item.icon}
              className={styles.navButton}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </Button>
          ))}
        </nav>
        <main className={styles.content}>{children}</main>
      </div>
    </div>
  );
}
